import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { downloadBlob } from '../utils/mp3Recorder';
import { encodeWav, takeFileName } from '../utils/wavFile';
import { sectionAudio, takeCovers } from '../utils/syncedTake';
import { createSyncedCapture, highlightedSection } from './desktop/syncedCapture';
import { onAudioDeviceChange, openSingingMic, routeToOutput } from './desktop/audioDevices';
import { logRecording, spoken, barsOf } from './desktop/practiceLog';
import '../styles/scratch-recorder.css';

// Captures raw float samples off the audio thread. Recording straight to PCM
// keeps the lossy Opus encoder out of the path entirely, which also means
// playback needs no decode step. Each chunk is stamped with the context
// frame of its first sample (a gap in the frames starts a new chunk), so
// the take can be lined up with the song.
const WORKLET_SOURCE = `
class ScratchRecorderProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this._chunks = [];
    this._length = 0;
    this._frame = 0;
    this._next = 0;
    this._recording = false;
    this.port.onmessage = (event) => {
      if (event.data === 'start') {
        this._chunks = [];
        this._length = 0;
        this._recording = true;
      } else if (event.data === 'stop') {
        this._recording = false;
        this._flush(true);
      }
    };
  }

  _flush(final) {
    if (this._length === 0) {
      if (final) this.port.postMessage({ samples: null, final: true });
      return;
    }
    const merged = new Float32Array(this._length);
    let offset = 0;
    for (let i = 0; i < this._chunks.length; i++) {
      merged.set(this._chunks[i], offset);
      offset += this._chunks[i].length;
    }
    this._chunks = [];
    this._length = 0;
    this.port.postMessage({ samples: merged, frame: this._frame, final: !!final }, [merged.buffer]);
  }

  process(inputs) {
    if (this._recording) {
      const channel = inputs[0] && inputs[0][0];
      if (channel && channel.length) {
        if (this._length > 0 && currentFrame !== this._next) this._flush(false);
        if (this._length === 0) this._frame = currentFrame;
        this._chunks.push(new Float32Array(channel));
        this._length += channel.length;
        this._next = currentFrame + channel.length;
        if (this._length >= 4096) this._flush(false);
      }
    }
    return true;
  }
}
registerProcessor('scratch-recorder', ScratchRecorderProcessor);
`;

// Normalisation aims just under full scale and refuses to chase silence,
// so an empty take does not get amplified into a wall of room noise.
const TARGET_PEAK = 0.89;
const MAX_BOOST = 12;
const SILENCE_FLOOR = 0.0005;
// How each way of recording is named in the activity log
const MODE_NAMES = { toggle: 'Record', hold: 'Hold to record', playback: 'Record with playback' };
// "Record with playback" gives up if the song has not started by then
// (nothing to play, or not signed in)
const PLAYBACK_START_TIMEOUT_MS = 3000;
const MIC_ERROR = 'Microphone unavailable — check browser permissions';

/**
 * The scratch recorder: record yourself and hear it straight back. Three
 * ways to record, each its own button:
 * - toggle: click to start, click again to stop;
 * - hold: records while the button is held down;
 * - playback: starts the song with a one-bar count-in and records until
 *   playback stops (Stop, Esc, or the end of the song).
 * A held take plays back as soon as the button is released; the others
 * wait for Play. While the song plays during a recording, the take is
 * lined up with it (syncedCapture.js), so with a section highlighted Play
 * plays only my singing of that section. The latest take can be downloaded
 * as a WAV. The engine lives in the practice shell, so a take survives the
 * panel being collapsed. The mic is opened on first use and kept warm while
 * practising, so later takes start without a getUserMedia round trip;
 * leaving practice releases it, and a new song drops the take.
 */
export function useScratchRecorder({ active, fileName, inPractice, onPlay, onStop, controls }) {
  const [mode, setMode] = useState(null); // 'toggle' | 'hold' | 'playback' while recording
  const [isRecording, setIsRecording] = useState(false);
  const [hasTake, setHasTake] = useState(false);
  const [isPlayingBack, setIsPlayingBack] = useState(false);
  const [micError, setMicError] = useState(null);

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const gainNodeRef = useRef(null);
  const workletReadyRef = useRef(false);
  const workletNodeRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const muteNodeRef = useRef(null);

  // Raw take plus the normalisation gain playback applies to it
  const takeBufferRef = useRef(null);
  const takeGainRef = useRef(1);
  const pendingChunksRef = useRef([]);
  const playbackSourceRef = useRef(null);
  // The take lined up with the song (null if the song did not play), and
  // the lining up of the recording in progress
  const [takeSync, setTakeSync] = useState(null);
  const takeSyncRef = useRef(null);
  const syncRef = useRef(null);
  const controlsRef = useRef(controls);
  controlsRef.current = controls;

  // The recording asked for, as a token: a stop (or a new request) while the
  // mic is still opening replaces it, so the late start knows to back off
  const requestRef = useRef(null);
  const capturingRef = useRef(false);
  // Set when a recording is dropped rather than kept (new song, leaving)
  const discardRef = useRef(false);
  // Set when a hold ends, so its take plays back once it is ready
  const playOnFinishRef = useRef(false);
  // Waiting for the worklet's last chunk after a stop
  const awaitingFinalRef = useRef(false);
  // How the take being recorded was started (for the activity log)
  const takeModeRef = useRef(null);
  // A different mic was chosen during a recording: switch once it is over
  const micStaleRef = useRef(false);
  const playbackSeenRef = useRef(false);
  const playbackTimerRef = useRef(null);
  const transportRef = useRef({ inPractice, onPlay, onStop });
  transportRef.current = { inPractice, onPlay, onStop };

  const stopPlayback = useCallback(() => {
    const source = playbackSourceRef.current;
    if (source) {
      source.onended = null;
      try {
        source.stop();
      } catch (e) {
        // Already finished
      }
      playbackSourceRef.current = null;
    }
    setIsPlayingBack(false);
  }, []);

  // `auto`: played by itself after Hold to record (not logged)
  const playTake = useCallback((auto) => {
    let buffer = takeBufferRef.current;
    const ctx = audioCtxRef.current;
    if (!buffer || !ctx) return;
    stopPlayback();
    if (ctx.state === 'suspended') ctx.resume();

    // With a section highlighted, only my singing of it
    const c = controlsRef.current;
    const synced = takeSyncRef.current;
    const section = synced && highlightedSection(c);
    const singing = section && sectionAudio(synced, section.from, section.to);
    if (singing) {
      buffer = ctx.createBuffer(1, singing.length, synced.sampleRate);
      buffer.copyToChannel(singing, 0);
    }
    if (auto !== true) {
      const bars = singing ? barsOf(c?.selectedRange) : null;
      logRecording({
        user: c?.user,
        fileName: c?.fileName,
        description: bars ? `Played back my singing of ${bars} (scratch take)` : 'Played back the scratch take',
        metadata: { event: 'play_take', section: Boolean(singing), bars }
      });
    }

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(gainNodeRef.current);
    source.onended = () => {
      if (playbackSourceRef.current === source) {
        playbackSourceRef.current = null;
        setIsPlayingBack(false);
      }
    };
    playbackSourceRef.current = source;
    setIsPlayingBack(true);
    source.start();
  }, [stopPlayback]);

  const downloadTake = useCallback(() => {
    const buffer = takeBufferRef.current;
    if (!buffer) return;
    const blob = encodeWav(buffer.getChannelData(0), buffer.sampleRate, takeGainRef.current);
    downloadBlob(blob, takeFileName(fileName));
    logRecording({
      user: controlsRef.current?.user,
      fileName,
      description: `Saved the scratch take (${spoken(buffer.duration)})`,
      metadata: { event: 'save_take', seconds: Math.round(buffer.duration) }
    });
  }, [fileName]);

  // Lets go of the mic (the next recording opens it again)
  const closeMic = useCallback(() => {
    if (workletNodeRef.current) {
      workletNodeRef.current.port.onmessage = null;
      workletNodeRef.current.disconnect();
      workletNodeRef.current = null;
    }
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }
    if (muteNodeRef.current) {
      muteNodeRef.current.disconnect();
      muteNodeRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  // One static gain for the whole take: raises the level the way AGC would,
  // without riding it up and down and flattening the dynamics as you sing.
  const finaliseTake = useCallback(() => {
    const ctx = audioCtxRef.current;
    const chunks = pendingChunksRef.current;
    pendingChunksRef.current = [];
    awaitingFinalRef.current = false;
    if (micStaleRef.current) {
      micStaleRef.current = false;
      closeMic();
    }
    setIsRecording(false);
    const playOnFinish = playOnFinishRef.current;
    playOnFinishRef.current = false;
    const capture = syncRef.current;
    syncRef.current = null;
    if (discardRef.current) {
      discardRef.current = false;
      capture?.cancel();
      return;
    }
    if (!ctx || chunks.length === 0) {
      capture?.cancel();
      return;
    }
    const synced = capture ? capture.finish() : null;
    takeSyncRef.current = synced;
    setTakeSync(synced);

    let length = 0;
    for (let i = 0; i < chunks.length; i++) length += chunks[i].length;
    const merged = new Float32Array(length);
    let offset = 0;
    let peak = 0;
    for (let i = 0; i < chunks.length; i++) {
      merged.set(chunks[i], offset);
      offset += chunks[i].length;
    }
    for (let i = 0; i < merged.length; i++) {
      const value = Math.abs(merged[i]);
      if (value > peak) peak = value;
    }

    const buffer = ctx.createBuffer(1, merged.length, ctx.sampleRate);
    buffer.copyToChannel(merged, 0);
    takeBufferRef.current = buffer;

    const gain = peak < SILENCE_FLOOR ? 1 : Math.min(MAX_BOOST, TARGET_PEAK / peak);
    takeGainRef.current = gain;
    if (gainNodeRef.current) gainNodeRef.current.gain.value = gain;

    console.log(
      `[recorder] take ${buffer.duration.toFixed(1)}s peak=${peak.toFixed(3)} ` +
      `gain=${gain.toFixed(1)}x${gain >= MAX_BOOST ? ' (CAPPED - input too quiet)' : ''}`
    );

    setHasTake(true);
    const mode = takeModeRef.current;
    logRecording({
      user: controlsRef.current?.user,
      fileName: controlsRef.current?.fileName,
      description: `Recorded a ${spoken(buffer.duration)} scratch take (${MODE_NAMES[mode] || 'Record'}${synced ? ', with the song' : ''})`,
      metadata: { event: 'take', mode, seconds: Math.round(buffer.duration), synced: Boolean(synced) }
    });
    // Hold to record plays the take back on release; the other ways keep
    // it for the Play button
    if (playOnFinish) playTake(true);
  }, [playTake, closeMic]);

  // Built lazily inside a user gesture so the context is allowed to start
  const ensureGraph = useCallback(async () => {
    if (!audioCtxRef.current) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      const ctx = new Ctx();
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      audioCtxRef.current = ctx;
      gainNodeRef.current = gain;
      routeToOutput(ctx);
    }
    const ctx = audioCtxRef.current;
    if (ctx.state === 'suspended') await ctx.resume();

    if (!workletReadyRef.current) {
      const url = URL.createObjectURL(new Blob([WORKLET_SOURCE], { type: 'application/javascript' }));
      try {
        await ctx.audioWorklet.addModule(url);
        workletReadyRef.current = true;
      } finally {
        URL.revokeObjectURL(url);
      }
    }
    return ctx;
  }, []);

  const ensureCapture = useCallback(() => {
    if (workletNodeRef.current) return workletNodeRef.current;
    const ctx = audioCtxRef.current;
    const source = ctx.createMediaStreamSource(streamRef.current);
    const node = new AudioWorkletNode(ctx, 'scratch-recorder');
    // A worklet only runs while it reaches the destination, so route it there
    // through a silent gain rather than letting the mic into the speakers
    const mute = ctx.createGain();
    mute.gain.value = 0;
    source.connect(node);
    node.connect(mute);
    mute.connect(ctx.destination);

    node.port.onmessage = (event) => {
      const { samples, frame, final } = event.data;
      if (samples) {
        pendingChunksRef.current.push(samples);
        syncRef.current?.push(samples, frame);
      }
      if (final) finaliseTake();
    };

    sourceNodeRef.current = source;
    workletNodeRef.current = node;
    muteNodeRef.current = mute;
    return node;
  }, [finaliseTake]);

  // Resolves true once the recording is running
  const start = useCallback(async (nextMode) => {
    if (requestRef.current) return false;
    const request = { mode: nextMode };
    requestRef.current = request;
    takeModeRef.current = nextMode;
    setMode(nextMode);
    stopPlayback();

    try {
      const ctx = await ensureGraph();
      if (!ctx) throw new Error('no audio context');

      if (!streamRef.current) {
        // The chosen mic, with voice-call DSP off on purpose. Noise
        // suppression gates sustained vowels, auto gain flattens dynamics and
        // echo cancellation smears the signal - all fine for speech, all
        // wrong for singing. The level auto gain used to provide is put back
        // by normalising each take instead.
        streamRef.current = await openSingingMic();

        const track = streamRef.current.getAudioTracks()[0];
        const settings = track.getSettings ? track.getSettings() : {};
        console.log(
          `[recorder] mic "${track.label}" ${settings.sampleRate || ctx.sampleRate}Hz ` +
          `ch=${settings.channelCount} AEC=${settings.echoCancellation} ` +
          `NS=${settings.noiseSuppression} AGC=${settings.autoGainControl}`
        );
      }
    } catch (error) {
      if (requestRef.current === request) {
        requestRef.current = null;
        setMode(null);
      }
      setMicError(MIC_ERROR);
      return false;
    }

    // Stopped (or the hold released) while the mic was opening
    if (requestRef.current !== request) return false;

    setMicError(null);
    pendingChunksRef.current = [];
    syncRef.current?.cancel();
    syncRef.current = controlsRef.current?.audioHook ? createSyncedCapture({ context: audioCtxRef.current, controls: controlsRef.current }) : null;
    ensureCapture().port.postMessage('start');
    capturingRef.current = true;
    setIsRecording(true);
    return true;
  }, [ensureCapture, ensureGraph, stopPlayback]);

  const stop = useCallback(() => {
    if (!requestRef.current) return;
    const stoppedMode = requestRef.current.mode;
    requestRef.current = null;
    setMode(null);
    clearTimeout(playbackTimerRef.current);
    playbackSeenRef.current = false;
    if (capturingRef.current && workletNodeRef.current) {
      capturingRef.current = false;
      playOnFinishRef.current = stoppedMode === 'hold';
      // finaliseTake runs when the worklet flushes its tail
      awaitingFinalRef.current = true;
      workletNodeRef.current.port.postMessage('stop');
    }
  }, []);

  const toggle = useCallback(() => {
    const current = requestRef.current;
    if (current?.mode === 'toggle') stop();
    else if (!current) start('toggle');
  }, [start, stop]);

  const holdStart = useCallback(() => { start('hold'); }, [start]);
  const holdEnd = useCallback(() => {
    if (requestRef.current?.mode === 'hold') stop();
  }, [stop]);

  // Start recording, then the song with a count-in; stopping the song stops
  // the recording. Pressed again while it runs, it stops the song.
  const recordWithPlayback = useCallback(async () => {
    const current = requestRef.current;
    if (current?.mode === 'playback') {
      if (transportRef.current.inPractice) transportRef.current.onStop();
      else stop();
      return;
    }
    if (current || transportRef.current.inPractice) return;
    if (!(await start('playback'))) return;
    playbackSeenRef.current = false;
    transportRef.current.onPlay({ countOff: true });
    playbackTimerRef.current = setTimeout(() => {
      if (requestRef.current?.mode === 'playback' && !playbackSeenRef.current) stop();
    }, PLAYBACK_START_TIMEOUT_MS);
  }, [start, stop]);

  useEffect(() => {
    if (requestRef.current?.mode !== 'playback') return;
    if (inPractice) playbackSeenRef.current = true;
    else if (playbackSeenRef.current) stop();
  }, [inPractice, mode, stop]);

  // Drop whatever is being recorded and the last take
  const dropTake = useCallback(() => {
    if (capturingRef.current) discardRef.current = true;
    stop();
    stopPlayback();
    takeBufferRef.current = null;
    syncRef.current?.cancel();
    syncRef.current = null;
    takeSyncRef.current = null;
    setTakeSync(null);
    setHasTake(false);
  }, [stop, stopPlayback]);

  // A different song drops the take
  useEffect(() => {
    dropTake();
  }, [fileName, dropTake]);

  // Leaving practice (or unmounting) releases the mic and the take
  const release = useCallback(() => {
    dropTake();
    clearTimeout(playbackTimerRef.current);
    closeMic();
    pendingChunksRef.current = [];
    capturingRef.current = false;
    awaitingFinalRef.current = false;
    micStaleRef.current = false;
    discardRef.current = false;
    setIsRecording(false);
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
      gainNodeRef.current = null;
      workletReadyRef.current = false;
    }
  }, [dropTake, closeMic]);

  useEffect(() => {
    if (!active) release();
  }, [active, release]);

  // A different output is used at once; a different mic from the next
  // recording on
  useEffect(() => onAudioDeviceChange((kind) => {
    if (kind === 'output') {
      routeToOutput(audioCtxRef.current);
    } else if (kind === 'input') {
      if (requestRef.current || capturingRef.current || awaitingFinalRef.current) micStaleRef.current = true;
      else closeMic();
    }
  }), [closeMic]);
  useEffect(() => release, [release]);

  // Whether Play would play just my singing of the highlighted section
  const section = active ? highlightedSection(controls) : null;
  const sectionFrom = section ? section.from : null;
  const sectionTo = section ? section.to : null;
  const playsSection = useMemo(
    () => Boolean(takeSync && sectionFrom !== null && takeCovers(takeSync, sectionFrom, sectionTo)),
    [takeSync, sectionFrom, sectionTo]
  );

  return {
    mode,
    isRecording,
    hasTake,
    isPlayingBack,
    playsSection,
    micError,
    canRecordWithPlayback: mode === 'playback' || (!mode && !inPractice),
    toggle,
    holdStart,
    holdEnd,
    recordWithPlayback,
    playTake,
    stopPlayback,
    downloadTake
  };
}

/**
 * The recorder's buttons, driven by useScratchRecorder: Record / Stop,
 * Hold to record, and Record with playback (R), then the last take.
 */
const ScratchRecorder = ({ recorder: r }) => {
  const busy = r.mode !== null;
  const recording = (m) => r.mode === m;

  const handleHoldDown = (event) => {
    event.preventDefault();
    // Keeps the release on this button even if the pointer drifts off it
    if (event.currentTarget.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (e) {
        // Capture is a nicety, not a requirement
      }
    }
    r.holdStart();
  };

  // Space / Enter hold the button too; keep them from reaching the
  // practice shell's Space = play/pause
  const handleHoldKey = (down) => (event) => {
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    event.stopPropagation();
    if (down && !event.repeat) r.holdStart();
    else if (!down) r.holdEnd();
  };

  return (
    <div className="scratch-recorder">
      <div className="scratch-row">
        <button
          type="button"
          className={`scratch-btn${recording('toggle') ? ' recording' : ''}`}
          onClick={r.toggle}
          disabled={busy && !recording('toggle')}
          title={recording('toggle') ? 'Stop recording' : 'Start recording; click again to stop'}
        >
          {recording('toggle') ? '■ Stop' : '● Record'}
        </button>
        <button
          type="button"
          className={`scratch-btn scratch-hold${recording('hold') ? ' recording' : ''}`}
          onPointerDown={handleHoldDown}
          onPointerUp={r.holdEnd}
          onPointerCancel={r.holdEnd}
          onKeyDown={handleHoldKey(true)}
          onKeyUp={handleHoldKey(false)}
          onContextMenu={(e) => e.preventDefault()}
          disabled={busy && !recording('hold')}
          title="Records while held; plays it back when you let go"
        >
          {recording('hold') ? '● Release to stop' : '🎤 Hold to record'}
        </button>
      </div>
      <button
        type="button"
        className={`scratch-btn scratch-wide${recording('playback') ? ' recording' : ''}`}
        onClick={r.recordWithPlayback}
        disabled={(busy && !recording('playback')) || !r.canRecordWithPlayback}
        title={recording('playback')
          ? 'Stop the song and the recording (R)'
          : 'Start the song with a one-bar count-in and record until it stops (R)'}
      >
        {recording('playback') ? '■ Stop' : '● Record with playback'}
        <kbd>R</kbd>
      </button>
      {r.hasTake && (
        <div className="scratch-take">
          <span>Last take</span>
          <button
            type="button"
            className={`scratch-btn${r.isPlayingBack ? ' playing' : ''}`}
            onClick={r.isPlayingBack ? r.stopPlayback : r.playTake}
            disabled={busy}
            title={r.playsSection ? 'Play my singing of the highlighted section' : 'Play the take'}
          >
            {r.isPlayingBack ? '■ Stop' : (r.playsSection ? '▶ Play section' : '▶ Play')}
          </button>
          <button type="button" className="scratch-btn" onClick={r.downloadTake} disabled={busy} title="Save this recording as a WAV file">
            {'⬇ Save recording'}
          </button>
        </div>
      )}
      {r.micError && <div className="record-error">{r.micError}</div>}
    </div>
  );
};

export default ScratchRecorder;
